import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Stack,
  Group,
  Button,
  Text,
  Paper,
  Badge,
  ActionIcon,
  Alert,
  TextInput,
  NumberInput,
  Divider,
  Image,
  SimpleGrid,
  Box,
  Skeleton,
} from '@mantine/core';
import { ResponsiveDrawer } from '../UI/ResponsiveDrawer';
import { ResponsiveSelect } from '../UI/ResponsiveSelect';
import {
  IconAlertCircle,
  IconCheck,
  IconX,
  IconEdit,
  IconEye,
  IconPhoto,
  IconZoomIn,
  IconZoomOut,
  IconFocusCentered,
  IconChevronLeft,
  IconChevronRight,
  IconChecklist,
} from '@tabler/icons-react';
import { DrawerHeader } from '../UI/DrawerHeader';
import { VideoPlayer } from '../UI/VideoPlayer';
import { useAuth } from '../../hooks/useAuth';
import { notifications } from '@mantine/notifications';
import { PantryItem, PantryCategory, PantryItemType } from '../../types/pantry';
import { getPantryItemsNeedingReview, approvePantryItem, updatePantryItem, deletePantryItem, approveAllPantryItems, rejectAllPantryItems } from '../../services/pantryService';
import { LoadingScreen } from '../Layout/LoadingScreen';

const isVideoUrl = (url: string) =>
  /\.(webm|mp4|mov|avi|mkv)(\?|$)/i.test(url) || /video%2F/i.test(url) || /\/video\//i.test(url);

interface PantryReviewModalProps {
  opened: boolean;
  onClose: () => void;
  onReviewCompleted: () => void;
}

interface ReviewItemState extends PantryItem {
  isEditing: boolean;
  editData: {
    displayName: string;
    quantity?: number;
    unit?: string;
    category?: PantryCategory;
    type: PantryItemType;
    notes?: string;
  };
}

const SourceThumbnail: React.FC<{ url: string; index: number; onClick: () => void }> = ({ url, index, onClick }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <Paper
      p={2}
      withBorder
      radius="lg"
      style={{
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        aspectRatio: '1',
      }}
      onClick={onClick}
    >
      {!loaded && (
        <Skeleton
          style={{ position: 'absolute', inset: 0 }}
          radius={0}
          animate
        />
      )}
      {isVideoUrl(url) ? (
        <video
          src={url}
          muted
          playsInline
          preload="metadata"
          onLoadedData={() => setLoaded(true)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: loaded ? 1 : 0,
          }}
        />
      ) : (
        <Image
          src={url}
          alt={`Source image ${index + 1}`}
          onLoad={() => setLoaded(true)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: loaded ? 1 : 0,
          }}
          fallbackSrc="/placeholder-image.png"
        />
      )}
      {loaded && (
        <ActionIcon
          size="xs"
          variant="filled"
          color="dark"
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            opacity: 0.8,
          }}
        >
          <IconZoomIn size={12} />
        </ActionIcon>
      )}
    </Paper>
  );
};

export const PantryReviewModal: React.FC<PantryReviewModalProps> = ({
  opened,
  onClose,
  onReviewCompleted,
}) => {
  const [items, setItems] = useState<ReviewItemState[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [processingItems, setProcessingItems] = useState<Map<string, 'approve' | 'delete' | 'save'>>(new Map());
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [currentImageUrls, setCurrentImageUrls] = useState<string[]>([]);
  const [imageZoom, setImageZoom] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isImageOverflowing, setIsImageOverflowing] = useState(false);
  const { user } = useAuth();

  const categories: { value: PantryCategory; label: string }[] = [
    { value: 'produce', label: 'Produce' },
    { value: 'meat', label: 'Meat' },
    { value: 'dairy', label: 'Dairy' },
    { value: 'pantry_dry_goods', label: 'Pantry & dry goods' },
    { value: 'spices_seasonings', label: 'Spices & seasonings' },
    { value: 'frozen', label: 'Frozen' },
    { value: 'bakery', label: 'Bakery' },
    { value: 'beverages', label: 'Beverages' },
    { value: 'other', label: 'Other' },
  ];

  const units = [
    {
      group: 'Volume',
      items: [
        { value: 'dash', label: 'dash' },
        { value: 'pinch', label: 'pinch' },
        { value: 'teaspoon', label: 'tsp' },
        { value: 'tablespoon', label: 'tbsp' },
        { value: 'fluid ounce', label: 'fl oz' },
        { value: 'cup', label: 'cup' },
        { value: 'pint', label: 'pt' },
        { value: 'quart', label: 'qt' },
        { value: 'gallon', label: 'gal' },
      ]
    },
    {
      group: 'Weight',
      items: [
        { value: 'ounce', label: 'oz' },
        { value: 'pound', label: 'lb' },
      ]
    },
    {
      group: 'Count/Container',
      items: [
        { value: 'each', label: 'each' },
        { value: 'whole', label: 'whole' },
        { value: 'piece', label: 'piece' },
        { value: 'slice', label: 'slice' },
        { value: 'clove', label: 'clove' },
        { value: 'can', label: 'can' },
        { value: 'bottle', label: 'bottle' },
        { value: 'jar', label: 'jar' },
        { value: 'package', label: 'package' },
        { value: 'bunch', label: 'bunch' },
        { value: 'head', label: 'head' },
        { value: 'stick', label: 'stick' },
      ]
    }
  ];

  const fetchReviewItems = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      const token = await user.getIdToken();
      const response = await getPantryItemsNeedingReview(token);
      
      const reviewItems: ReviewItemState[] = response.items.map(item => ({
        ...item,
        isEditing: false,
        editData: {
          displayName: item.displayName,
          quantity: item.quantity,
          unit: item.unit,
          category: item.category,
          type: item.type,
          notes: item.notes,
        },
      }));
      
      setItems(reviewItems);
    } catch (error: any) {
      console.error('Error fetching review items:', error);
      setError(error.message || 'Failed to load items for review');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (opened) {
      fetchReviewItems();
    }
  }, [opened, fetchReviewItems]);

  const handleApprove = useCallback(async (item: ReviewItemState) => {
    if (!user) return;

    setProcessingItems(prev => new Map(prev).set(item.id, 'approve'));

    try {
      const token = await user.getIdToken();
      await approvePantryItem(token, item.id);
      
      // Remove from review list
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch (error: any) {
      console.error('Error approving item:', error);
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to approve item',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
        autoClose: 7000,
      });
    } finally {
      setProcessingItems(prev => {
        const newMap = new Map(prev);
        newMap.delete(item.id);
        return newMap;
      });
    }
  }, [user]);

  const handleDelete = useCallback(async (item: ReviewItemState) => {
    if (!user) return;

    setProcessingItems(prev => new Map(prev).set(item.id, 'delete'));

    try {
      const token = await user.getIdToken();
      await deletePantryItem(token, item.id);
      
      // Remove from review list
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch (error: any) {
      console.error('Error deleting item:', error);
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to remove item',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
        autoClose: 7000,
      });
    } finally {
      setProcessingItems(prev => {
        const newMap = new Map(prev);
        newMap.delete(item.id);
        return newMap;
      });
    }
  }, [user]);

  const handleEdit = useCallback((itemId: string) => {
    setItems(prev => prev.map(item => 
      item.id === itemId 
        ? { ...item, isEditing: true }
        : item
    ));
  }, []);

  const handleCancelEdit = useCallback((itemId: string) => {
    setItems(prev => prev.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          isEditing: false,
          editData: {
            displayName: item.displayName,
            quantity: item.quantity,
            unit: item.unit,
            category: item.category,
            type: item.type,
            notes: item.notes,
          },
        };
      }
      return item;
    }));
  }, []);

  const handleSaveEdit = useCallback(async (item: ReviewItemState) => {
    if (!user) return;

    setProcessingItems(prev => new Map(prev).set(item.id, 'save'));

    try {
      const token = await user.getIdToken();
      
      // Update the item with edited data and approve it
      await updatePantryItem(token, item.id, {
        ...item.editData,
        needsReview: false, // Approve it by removing the review flag
      });
      
      // Remove from review list
      setItems(prev => prev.filter(i => i.id !== item.id));
    } catch (error: any) {
      console.error('Error updating item:', error);
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update item',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
        autoClose: 7000,
      });
    } finally {
      setProcessingItems(prev => {
        const newMap = new Map(prev);
        newMap.delete(item.id);
        return newMap;
      });
    }
  }, [user]);

  const handleEditDataChange = useCallback((itemId: string, field: string, value: any) => {
    setItems(prev => prev.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          editData: {
            ...item.editData,
            [field]: value,
          },
        };
      }
      return item;
    }));
  }, []);

  const [bulkProcessing, setBulkProcessing] = useState<'approve' | 'reject' | null>(null);

  const handleApproveAll = useCallback(async () => {
    if (!user || items.length === 0) return;

    setBulkProcessing('approve');
    try {
      const token = await user.getIdToken();
      const itemIds = items.map(item => item.id);
      await approveAllPantryItems(token, itemIds);
      setItems([]);
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to approve all items',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
        autoClose: 7000,
      });
    } finally {
      setBulkProcessing(null);
    }
  }, [user, items]);

  const handleRejectAll = useCallback(async () => {
    if (!user || items.length === 0) return;

    setBulkProcessing('reject');
    try {
      const token = await user.getIdToken();
      const itemIds = items.map(item => item.id);
      await rejectAllPantryItems(token, itemIds);
      setItems([]);
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to reject all items',
        color: 'red',
        icon: <IconAlertCircle size={18} />,
        autoClose: 7000,
      });
    } finally {
      setBulkProcessing(null);
    }
  }, [user, items]);

  const handleClose = useCallback(() => {
    if (items.length === 0) {
      onReviewCompleted();
    }
    onClose();
  }, [items.length, onClose, onReviewCompleted]);

  const handleImageClose = useCallback(() => {
    setSelectedImage(null);
    setSelectedImageIndex(0);
    setCurrentImageUrls([]);
    setImageZoom(1);
    setImagePosition({ x: 0, y: 0 });
    setIsImageOverflowing(false);
  }, []);

  const handleZoomIn = useCallback(() => {
    setImageZoom(prev => {
      const newZoom = Math.min(prev + 0.5, 3);
      setIsImageOverflowing(newZoom > 1);
      return newZoom;
    });
  }, []);

  const handleZoomOut = useCallback(() => {
    setImageZoom(prev => {
      const newZoom = Math.max(prev - 0.5, 0.5);
      // Will recalculate on next render via image onLoad
      return newZoom;
    });
  }, []);

  const handleResetZoom = useCallback(() => {
    setImageZoom(1);
    setImagePosition({ x: 0, y: 0 });
  }, []);

  const handlePrevImage = useCallback(() => {
    if (currentImageUrls.length > 1 && selectedImageIndex > 0) {
      const newIndex = selectedImageIndex - 1;
      setSelectedImageIndex(newIndex);
      setSelectedImage(currentImageUrls[newIndex]);
      setImageZoom(1);
      setImagePosition({ x: 0, y: 0 });
    }
  }, [currentImageUrls, selectedImageIndex]);

  const handleNextImage = useCallback(() => {
    if (currentImageUrls.length > 1 && selectedImageIndex < currentImageUrls.length - 1) {
      const newIndex = selectedImageIndex + 1;
      setSelectedImageIndex(newIndex);
      setSelectedImage(currentImageUrls[newIndex]);
      setImageZoom(1);
      setImagePosition({ x: 0, y: 0 });
    }
  }, [currentImageUrls, selectedImageIndex]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
  }, [imagePosition]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);


  if (loading && items.length === 0) {
    return (
      <ResponsiveDrawer opened={opened} onClose={handleClose} size="lg">
        <LoadingScreen />
      </ResponsiveDrawer>
    );
  }

  return (
    <ResponsiveDrawer
      opened={opened}
      onClose={handleClose}
      title={
        <DrawerHeader
          icon={<IconChecklist size={18} />}
          title="Review pantry items"
        />
      }
      size="xl"
    >
      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error"
            color="red"
            variant="light"
            radius="lg"
          >
            {error}
          </Alert>
        )}

        {items.length === 0 && !loading ? (
          <Paper p="xl" radius="lg" withBorder>
            <Stack gap="md" align="center">
              <IconCheck size={48} color="var(--mantine-color-green-6)" />
              <Text fw={500}>All items reviewed!</Text>
              <Text c="dimmed" size="sm" ta="center">
                There are no items that need review at this time.
              </Text>
            </Stack>
          </Paper>
        ) : (
          <>
            <Alert
              icon={<IconEye size={16} />}
              title="Items need review"
              color="blue"
              variant="light"
              radius="lg"
            >
              <Text size="sm">
                These imported items need your review.
                You can approve them as-is, edit them, or remove them entirely.
              </Text>
            </Alert>

            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500}>{items.length} item{items.length !== 1 ? 's' : ''} to review</Text>
                <Group gap="xs">
                  <Button
                    variant="light"
                    color="green"
                    size="xs"
                    leftSection={<IconCheck size={14} />}
                    onClick={handleApproveAll}
                    loading={bulkProcessing === 'approve'}
                    disabled={bulkProcessing !== null || processingItems.size > 0}
                  >
                    Approve all
                  </Button>
                  <Button
                    variant="light"
                    color="red"
                    size="xs"
                    leftSection={<IconX size={14} />}
                    onClick={handleRejectAll}
                    loading={bulkProcessing === 'reject'}
                    disabled={bulkProcessing !== null || processingItems.size > 0}
                  >
                    Reject all
                  </Button>
                </Group>
              </Group>

              <Stack gap="lg">
                {items.map((item) => (
                  <Paper key={item.id} p="md" radius="lg" withBorder>
                      <Stack gap="md">
                        {/* Header with name and action buttons */}
                        <Group justify="space-between" align="flex-start" wrap="nowrap">
                          {item.isEditing ? (
                            <TextInput
                              label="Item Name"
                              value={item.editData.displayName}
                              onChange={(e) => handleEditDataChange(item.id, 'displayName', e.target.value)}
                              placeholder="Enter item name"
                              required
                              style={{ flex: 1, minWidth: 0 }}
                            />
                          ) : (
                            <Text fw={500} size="lg" style={{ minWidth: 0, wordBreak: 'break-word' }}>{item.displayName}</Text>
                          )}

                          {!item.isEditing && (
                            <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(item.id)}
                                disabled={processingItems.has(item.id)}
                              >
                                <IconEdit size={16} />
                              </ActionIcon>
                              <ActionIcon
                                variant="subtle"
                                color="green"
                                onClick={() => handleApprove(item)}
                                loading={processingItems.get(item.id) === 'approve'}
                              >
                                <IconCheck size={16} />
                              </ActionIcon>
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                onClick={() => handleDelete(item)}
                                loading={processingItems.get(item.id) === 'delete'}
                              >
                                <IconX size={16} />
                              </ActionIcon>
                            </Group>
                          )}
                        </Group>

                        {/* Content section - full width */}
                        <Stack gap="xs">
                          <Group gap="xs">
                            <Badge
                              size="sm"
                              color={item.type === 'staple' ? 'green' : 'blue'}
                              variant="light"
                            >
                              {item.type === 'staple' ? 'Staple' : 'Ingredient'}
                            </Badge>
                            {item.type === 'ingredient' && item.quantity && item.unit && (
                              <Badge
                                size="sm"
                                color="gray"
                                variant="light"
                              >
                                {item.quantity} {item.unit}
                              </Badge>
                            )}
                          </Group>
                          
                          {item.extractionMetadata?.reasoning && (
                            <Text size="sm" c="dimmed">
                              Notes: {item.extractionMetadata.reasoning}
                            </Text>
                          )}
                          
                          {item.extractionMetadata?.sourceImageUrls && item.extractionMetadata.sourceImageUrls.length > 0 && (
                            <Stack gap="xs">
                              <Group gap="xs" align="center">
                                <IconPhoto size={16} />
                                <Text size="sm" fw={500} c="dimmed">
                                  Sources ({item.extractionMetadata.sourceImageUrls.length})
                                </Text>
                              </Group>
                              <SimpleGrid cols={3} spacing="xs" style={{ maxWidth: 200 }}>
                                {item.extractionMetadata.sourceImageUrls.map((url, index) => (
                                  <SourceThumbnail
                                    key={index}
                                    url={url}
                                    index={index}
                                    onClick={() => {
                                      setSelectedImage(url);
                                      setCurrentImageUrls(item.extractionMetadata!.sourceImageUrls!);
                                      setSelectedImageIndex(index);
                                      setImageZoom(1);
                                      setImagePosition({ x: 0, y: 0 });
                                    }}
                                  />
                                ))}
                              </SimpleGrid>
                            </Stack>
                          )}
                        </Stack>

                        {item.isEditing && (
                          <>
                            <Divider />
                            <Group grow>
                              <ResponsiveSelect
                                label="Category"
                                value={item.editData.category}
                                onChange={(value) => handleEditDataChange(item.id, 'category', value)}
                                data={categories}
                                placeholder="Select category"
                              />
                              <ResponsiveSelect
                                label="Type"
                                value={item.editData.type}
                                onChange={(value) => handleEditDataChange(item.id, 'type', value)}
                                data={[
                                  { value: 'staple', label: 'Staple' },
                                  { value: 'ingredient', label: 'Ingredient' },
                                ]}
                                required
                              />
                            </Group>

                            {item.editData.type === 'ingredient' && (
                              <Group grow>
                                <NumberInput
                                  label="Quantity"
                                  value={item.editData.quantity || ''}
                                  onChange={(value) => handleEditDataChange(item.id, 'quantity', value)}
                                  min={0}
                                  step={0.25}
                                  decimalScale={2}
                                  placeholder="Enter quantity"
                                />
                                <ResponsiveSelect
                                  label="Unit"
                                  value={item.editData.unit || ''}
                                  onChange={(value) => handleEditDataChange(item.id, 'unit', value)}
                                  placeholder="Select unit"
                                  data={units}
                                  searchable
                                  clearable
                                />
                              </Group>
                            )}


                            <Group gap="xs">
                              <Button
                                variant="filled"
                                size="sm"
                                onClick={() => handleSaveEdit(item)}
                                loading={processingItems.get(item.id) === 'save'}
                                disabled={!item.editData.displayName.trim()}
                              >
                                Save & Approve
                              </Button>
                              <Button
                                variant="subtle"
                                size="sm"
                                onClick={() => handleCancelEdit(item.id)}
                                disabled={processingItems.has(item.id)}
                              >
                                Cancel
                              </Button>
                            </Group>
                          </>
                        )}
                      </Stack>
                    </Paper>
                ))}
              </Stack>
            </Stack>
          </>
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={handleClose}>
            {items.length === 0 ? 'Done' : 'Close'}
          </Button>
        </Group>
      </Stack>

      {/* Image Enlargement Modal */}
      <Drawer
        opened={!!selectedImage}
        onClose={handleImageClose}
        title={
          <Group justify="space-between" w="100%">
            <Group gap="xs">
              <Text>{selectedImage && isVideoUrl(selectedImage) ? 'Source Video' : 'Source Image'}</Text>
              {currentImageUrls.length > 1 && (
                <Text size="sm" c="dimmed">
                  {selectedImageIndex + 1} of {currentImageUrls.length}
                </Text>
              )}
            </Group>
            <Group gap="xs">
              {currentImageUrls.length > 1 && (
                <>
                  <ActionIcon
                    variant="subtle"
                    onClick={handlePrevImage}
                    disabled={selectedImageIndex === 0}
                  >
                    <IconChevronLeft size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    onClick={handleNextImage}
                    disabled={selectedImageIndex === currentImageUrls.length - 1}
                  >
                    <IconChevronRight size={16} />
                  </ActionIcon>
                </>
              )}
              {selectedImage && !isVideoUrl(selectedImage) && (
                <>
                  <ActionIcon
                    variant="subtle"
                    onClick={handleZoomOut}
                    disabled={imageZoom <= 0.5}
                  >
                    <IconZoomOut size={16} />
                  </ActionIcon>
                  <Text size="sm" c="dimmed" w={60} ta="center">
                    {Math.round(imageZoom * 100)}%
                  </Text>
                  <ActionIcon
                    variant="subtle"
                    onClick={handleZoomIn}
                    disabled={imageZoom >= 3}
                  >
                    <IconZoomIn size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    onClick={handleResetZoom}
                  >
                    <IconFocusCentered size={16} />
                  </ActionIcon>
                </>
              )}
            </Group>
          </Group>
        }
        position="right"
        size="xl"
        styles={{
          body: {
            padding: 0,
            overflow: 'auto',
            height: '80vh',
            position: 'relative',
          },
        }}
      >
        {selectedImage && (
          <>
            <Box
              w="100%"
              style={{
                cursor: isVideoUrl(selectedImage) ? 'default' : isDragging ? 'grabbing' : 'grab',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                minHeight: '100%',
                padding: '16px',
              }}
              onMouseDown={isVideoUrl(selectedImage) ? undefined : handleMouseDown}
              onMouseMove={isVideoUrl(selectedImage) ? undefined : handleMouseMove}
              onMouseUp={isVideoUrl(selectedImage) ? undefined : handleMouseUp}
              onMouseLeave={isVideoUrl(selectedImage) ? undefined : handleMouseUp}
            >
            {isVideoUrl(selectedImage) ? (
              <VideoPlayer
                src={selectedImage}
                controls
                style={{
                  maxWidth: '100%',
                  maxHeight: 'calc(80vh - 80px)',
                  userSelect: 'none',
                }}
              />
            ) : (
              <Image
                src={selectedImage}
                alt="Source image"
                onLoad={(e) => {
                  const img = e.currentTarget;
                  const container = img.parentElement?.parentElement;
                  if (img && container) {
                    const imgRect = img.getBoundingClientRect();
                    const containerRect = container.getBoundingClientRect();
                    setIsImageOverflowing(
                      imgRect.height > containerRect.height - 32 || // 32px for padding
                      imgRect.width > containerRect.width - 32 ||
                      imageZoom > 1
                    );
                  }
                }}
                style={{
                  transform: `scale(${imageZoom}) translate(${imagePosition.x}px, ${imagePosition.y}px)`,
                  transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                  maxWidth: imageZoom > 1 ? 'none' : '100%',
                  height: 'auto',
                  userSelect: 'none',
                  pointerEvents: 'none',
                }}
                fallbackSrc="/placeholder-image.png"
              />
            )}
            </Box>
            
            {/* Scroll indicators */}
            <Box
              style={{
                position: 'absolute',
                bottom: '20px',
                right: '20px',
                background: 'rgba(0, 0, 0, 0.6)',
                borderRadius: '8px',
                padding: '8px 12px',
                color: 'white',
                fontSize: '12px',
                display: isImageOverflowing ? 'block' : 'none',
                pointerEvents: 'none',
              }}
            >
              Drag to pan • Scroll to zoom
            </Box>
          </>
        )}
      </Drawer>
    </ResponsiveDrawer>
  );
};
